/**
 * App.js
 *
 * Purpose:
 * - Root: theme, auth gate, global providers, and the router.
 * - The whole app is auth-gated by <AuthGate>: an unauthenticated visitor always
 *   lands on the login screen, a signed-in user who still owes a TOTP 2FA
 *   step-up (#158) on the MFA challenge screen, a pending user on the approval
 *   notice, and a user with no organization on onboarding — only then does the
 *   app shell render.
 * - The IAM / admin / settings pages live INSIDE the Dashboard shell (themed
 *   sidebar + navbar); see Dashboard.js routes. There is no separate /account
 *   layout any more.
 */

import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dashboard from './Dashboard';
import { TreeSyncProvider } from './contexts/TreeSyncContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ConflictPromptHost from './components/dialogs/ConflictPromptHost';
import { LoginPage, OnboardingPage, PendingApproval, MfaChallengePage } from './iam/pages';
import ConnectConsent from './iam/ConnectConsent';
import AcceptInvite from './iam/AcceptInvite';
import AppTheme from './shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
};

// Single shell route — Dashboard owns its own nested routes (including the
// /account/* IAM pages), so everything renders inside the themed sidebar layout.
// /connect is the MCP connector consent page — a chrome-free route (sibling to the
// Dashboard shell), still behind <AuthGate> so the user is signed in + approved.
const router = createBrowserRouter([
  { path: '/connect', element: <ConnectConsent /> },
  { path: '/accept-invite', element: <AcceptInvite /> },
  { path: '/*', element: <Dashboard /> },
]);

// Auth gate: decides login -> pending -> onboarding -> app. Lives inside
// AuthProvider so it can read the session.
function AuthGate({ children }) {
  const { loading, token, user, orgs, refreshError, mfaRequired } = useAuth();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!token) return <LoginPage />;
  // #158: a Supabase session at aal1 with a verified TOTP factor must step up
  // to aal2 before anything else (pending-approval status, org membership,
  // the app itself) — gate here, ahead of every other post-token check.
  if (mfaRequired) return <MfaChallengePage />;
  if (user && user.status !== 'approved') return <PendingApproval />;
  if (orgs.length === 0) return <OnboardingPage />;
  return (
    <>
      {/* #511: a transient (network/5xx) refresh() failure keeps the existing
          session instead of logging the user out — surface it here so it isn't
          silent, without unmounting the app underneath it. */}
      {refreshError && (
        <Alert severity="warning" data-testid="auth-refresh-warning" sx={{ borderRadius: 0 }}>
          {refreshError}
        </Alert>
      )}
      {children}
    </>
  );
}

function App(props) {
  // TreeSyncProvider must wrap the whole app so that the chat agent
  // (useChatAgent -> useRequirementsSync) and the requirements tree share the
  // same refresh trigger; without it tree auto-refresh silently no-ops.
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <AuthProvider>
        <TreeSyncProvider>
          <AuthGate>
            <RouterProvider router={router} />
          </AuthGate>
          {/* #579: the shared "someone else changed this" prompt for the non-editor
              update flows. Mounted ONCE here rather than per call site, because three
              of those flows live in hooks (useTreeState, useAiFunctions) or an inline
              tree callback that render no JSX of their own. Renders nothing until a
              409 actually asks it a question. */}
          <ConflictPromptHost />
        </TreeSyncProvider>
      </AuthProvider>
    </AppTheme>
  );
}

export default App;
